import React from 'react';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import lowerCase from 'lodash/lowerCase';
import upperFirst from 'lodash/upperFirst';
import { Box } from '@atlaskit/primitives';
import { Divider, EntitlementIcon, OpenSidebarButton } from '@css-xp/base';
import { Currency, NoData } from '@css-xp/ppc-common';
import { QUOTE_CONSTANTS } from '@css-xp/ppc-constants';
import { pluralize } from '@css-xp/ppc-utils';
import { Estimate, CustomPanel } from '@css-xp/ppc-purchase-experience';
import { QuoteIntegratedEntity } from './quote-integrated-entity';
import { QuoteOrderSummaryLineItem } from './quote-types';
import { QuoteProratedCharges } from './quote-prorated-charges';
import './quote-line-items.scss';

export type LineItemsProps = {
  lineItems: QuoteOrderSummaryLineItem[];
  isJiraFamily: boolean;
  loading: boolean;
  isAddOnChangesEnabled?: boolean;
  isProratedChangesEnabled?: boolean;
  currency?: string;
  isDesignIteration1Enabled?: boolean;
  isTWCPhase1Enabled?: boolean;
  isMultiInstanceAppsEnabled?: boolean;
};

const renderIncludedEntitlementsAction = ({
  lineItem,
  includedEntitlementsData,
  isDesignIteration1Enabled,
  isEntitlementAnApp,
}: {
  lineItem: QuoteOrderSummaryLineItem;
  includedEntitlementsData: QuoteOrderSummaryLineItem[];
  isDesignIteration1Enabled?: boolean;
  isEntitlementAnApp?: boolean;
}) => {
  if (!includedEntitlementsData || includedEntitlementsData.length === 0) return;
  return (
    <Box paddingBlockStart="space.025">
      <OpenSidebarButton
        sidebar={QUOTE_CONSTANTS.QUOTE_PAGE_SIDEBAR.INCLUDED_ENTITLEMENTS}
        sidebarOptions={
          isDesignIteration1Enabled
            ? { sideBarDistanceFromTop: '0px' }
            : {
                sidebarWidth: '20%',
                isOverlay: true,
              }
        }
        payload={{
          version: isDesignIteration1Enabled ? 2 : undefined,
          entitlementName: lineItem?.name,
          entitlementNumber: lineItem?.entitlementNumber,
          includedEntitlementsList: includedEntitlementsData?.map((instance: QuoteOrderSummaryLineItem) => ({
            productId: instance?.productKey,
            logoId: instance?.logoId,
            productName: instance?.name,
            cloudSiteHostname: instance?.cloudSite === QUOTE_CONSTANTS.NEW_CLOUD_SITE ? null : instance?.cloudSite,
            entitlementNumber: instance?.entitlementNumber,
          })),
          isEntitlementAnApp,
        }}
        spacing="compact"
        testId="quote-order-summary-included-entitlements-sidebar-button"
        label="Included entitlements"
      >
        {pluralize(includedEntitlementsData.length, 'linked subscription')}
      </OpenSidebarButton>
    </Box>
  );
};

export const LineItems: React.FC<LineItemsProps> = ({
  lineItems,
  isJiraFamily,
  loading,
  isAddOnChangesEnabled = false,
  isProratedChangesEnabled = false,
  currency = Currency.USD,
  isDesignIteration1Enabled = false,
  isTWCPhase1Enabled = false,
  isMultiInstanceAppsEnabled = false,
}) => {
  return (
    <>
      {lineItems.map((lineItem, index) => {
        return (
          <>
            {index !== 0 && !isJiraFamily && <Divider />}
            <div className="line-item-app-container" key={`${lineItem.lineItemId}`}>
              <div className="line-item-app-container-icon">
                <EntitlementIcon
                  productId={lineItem?.productKey}
                  className="avatar medium pt-4"
                  logoId={lineItem?.logoId}
                  isCloud
                />
              </div>
              <CustomPanel
                heading={
                  <>
                    <div className="line-item-app-container-selected-product-row-container">
                      <div className="existing-site-container">
                        <h4 className="line-item-app-container-selected-product-name" data-testid="selected-app-name">
                          {lineItem?.name}
                        </h4>
                        {isTWCPhase1Enabled && get(lineItem, 'collectionInstanceLineItems', []).length > 0 && (
                          <Box paddingBlockStart="space.025">
                            <OpenSidebarButton
                              sidebar={QUOTE_CONSTANTS.QUOTE_PAGE_SIDEBAR.INCLUDED_PRODUCTS}
                              sidebarOptions={
                                isDesignIteration1Enabled
                                  ? { sideBarDistanceFromTop: '0px' }
                                  : {
                                      sidebarWidth: '20%',
                                      isOverlay: true,
                                    }
                              }
                              payload={{
                                version: isDesignIteration1Enabled ? 2 : undefined,
                                productName: lineItem?.name,
                                entitlementNumber: lineItem?.entitlementNumber,
                                includedProductsList: lineItem?.collectionInstanceLineItems?.map(
                                  (instance: QuoteOrderSummaryLineItem) => ({
                                    productId: instance?.productKey,
                                    logoId: instance?.logoId,
                                    productName: instance?.name,
                                    cloudSiteHostname:
                                      instance?.cloudSite === QUOTE_CONSTANTS.NEW_CLOUD_SITE
                                        ? null
                                        : instance?.cloudSite,
                                    entitlementNumber: instance?.entitlementNumber,
                                  })
                                ),
                              }}
                              spacing="compact"
                              testId="quote-order-summary-included-products-sidebar-button"
                              label="Included products"
                            >
                              {pluralize(get(lineItem, 'collectionInstanceLineItems', []).length, 'included product')}
                            </OpenSidebarButton>
                          </Box>
                        )}
                        {isMultiInstanceAppsEnabled &&
                          renderIncludedEntitlementsAction({
                            lineItem,
                            includedEntitlementsData: get(lineItem, 'multiInstanceAppInstanceLineItems', []),
                            isDesignIteration1Enabled,
                            isEntitlementAnApp: true, // for multi-instance app instances, the entitlement will be an app
                          })}
                      </div>
                      <Estimate
                        currency={currency}
                        price={lineItem?.headerTotal}
                        loading={loading}
                        className={'line-item-app-container-selected-product-estimated-price'}
                        data-testid={`quote-lineitem-header-estimate-${lineItem.lineItemId}`}
                      />
                    </div>
                  </>
                }
                isDefaultExpanded={true}
              >
                <div className="line-item-app-container-selected-product">
                  {/* Entitlement Number */}
                  <div className="line-item-app-container-selected-product-row-container">
                    <p className="line-item-app-container-selected-product-pricing-details">Entitlement number</p>
                    <p className="line-item-app-container-selected-product-pricing-details">
                      {lineItem?.entitlementNumber || <NoData />}
                    </p>
                  </div>
                  {/* Plan */}
                  <div className="line-item-app-container-selected-product-row-container">
                    <p className="line-item-app-container-selected-product-pricing-details">
                      {`Plan: ${lineItem?.offeringName}`}
                      {!lineItem?.isFreeProduct && lineItem?.billingCycleLabel
                        ? ', ' + upperFirst(lowerCase(lineItem?.billingCycleLabel))
                        : ''}
                    </p>
                  </div>
                  {/* Start Date */}
                  {lineItem?.startDate !== undefined && (
                    <div className="line-item-app-container-selected-product-row-container">
                      <p className="line-item-app-container-selected-product-pricing-details">Start date</p>
                      <p className="line-item-app-container-selected-product-pricing-details">
                        {lineItem.startDate ? lineItem.startDate : <NoData />}
                      </p>
                    </div>
                  )}
                  {/* Renewal/End Date */}
                  {lineItem?.renewalOrEndDate !== undefined && (
                    <div className="line-item-app-container-selected-product-row-container">
                      <p className="line-item-app-container-selected-product-pricing-details">Renewal / End date</p>
                      <p className="line-item-app-container-selected-product-pricing-details">
                        {lineItem.renewalOrEndDate ? lineItem.renewalOrEndDate : <NoData />}
                      </p>
                    </div>
                  )}
                  {/* User tier */}
                  {!lineItem.isFreeProduct && (
                    <div className="line-item-app-container-selected-product-row-container">
                      <p className="line-item-app-container-selected-product-pricing-details">
                        {isAddOnChangesEnabled
                          ? `Quantity ${lineItem?.chargeElement ? `(${lineItem?.chargeElement})` : ''}`
                          : `User tier`}
                      </p>
                      <p className="line-item-app-container-selected-product-pricing-details">
                        {lineItem?.users || <NoData />}
                      </p>
                    </div>
                  )}
                  {/* List Price */}
                  <div className="line-item-app-container-selected-product-row-container">
                    <p className="line-item-app-container-selected-product-pricing-details">List price</p>
                    <Estimate
                      currency={currency}
                      price={lineItem.listPrice ?? 0}
                      loading={loading}
                      className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
                      data-testid={`quote-lineitem-list-price-${lineItem.lineItemId}`}
                    />
                  </div>
                  {/* Other Discounts */}
                  <div className="line-item-app-container-selected-product-row-container">
                    <p className="line-item-app-container-selected-product-pricing-details">Other discounts</p>
                    <Estimate
                      currency={currency}
                      price={lineItem.discountTotal ? -lineItem.discountTotal : 0}
                      loading={loading}
                      className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
                      data-testid={`quote-lineitem-other-discount-${lineItem.lineItemId}`}
                    />
                  </div>
                  {/* Credits */}
                  <div className="line-item-app-container-selected-product-row-container">
                    <p className="line-item-app-container-selected-product-pricing-details">Credits</p>
                    <Estimate
                      currency={currency}
                      price={lineItem.credits ? -lineItem.credits : 0}
                      loading={loading}
                      className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
                    />
                  </div>
                  {/* Partner Discount */}
                  <div className="line-item-app-container-selected-product-row-container">
                    <p
                      className="line-item-app-container-selected-product-pricing-details"
                      data-testid="quote-partner-margin"
                    >
                      Partner discount: {!lineItem.shouldHidePartnerMarginPercentage && `${lineItem?.marginPercent}%`}
                    </p>
                    <Estimate
                      currency={currency}
                      price={lineItem.marginTotal ? -lineItem.marginTotal : 0}
                      loading={loading}
                      className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
                    />
                  </div>
                  {/* Estimate incl. partner discount */}
                  <div
                    className="line-item-app-container-selected-product-row-container"
                    data-testid="quote-lineitem-estimate-with-margin-applied"
                  >
                    <h4 className="line-item-app-container-selected-product-estimate-partner-discount">
                      Estimate incl. partner discount
                    </h4>
                    <Estimate
                      currency={currency}
                      price={
                        defaultTo(lineItem.listPrice, 0) -
                        defaultTo(lineItem.discountTotal, 0) -
                        defaultTo(lineItem.credits, 0) -
                        defaultTo(lineItem.marginTotal, 0)
                      }
                      loading={loading}
                      className={'line-item-app-container-selected-product-estimate-partner-discount-currency'}
                    />
                  </div>
                  {/* Admin Fee */}
                  {lineItem?.adminFee ? (
                    <div className="line-item-app-container-selected-product-row-container">
                      <p className="line-item-app-container-selected-product-pricing-details">{`Administration fee (DST)`}</p>
                      <Estimate
                        currency={currency}
                        price={lineItem?.adminFee ?? 0}
                        loading={loading}
                        className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
                      />
                    </div>
                  ) : null}
                  {/* Tax */}
                  {lineItem?.itemLevelTaxInfo?.map(({ taxLabel, taxInformation }) => {
                    return (
                      <div
                        key={`tax-(${taxInformation?.taxPercent}-${taxLabel})`}
                        className="line-item-app-container-selected-product-row-container"
                      >
                        <p className="line-item-app-container-selected-product-pricing-details">{`Tax (${taxInformation?.taxPercent}% ${taxLabel})`}</p>
                        <Estimate
                          currency={currency}
                          price={taxInformation?.tax as number}
                          loading={loading}
                          className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
                        />
                      </div>
                    );
                  })}
                </div>
                {isProratedChangesEnabled && lineItem?.proratedCharges && (
                  <QuoteProratedCharges
                    proratedCharges={lineItem?.proratedCharges}
                    lineItemId={lineItem?.lineItemId}
                    loading={loading}
                    currency={currency}
                  />
                )}
                {/** grouping apps under products section */}
                {lineItem?.appLineItems && lineItem?.appLineItems.length > 0 && (
                  <QuoteIntegratedEntity
                    lineItems={lineItem.appLineItems}
                    lineItemId={lineItem.lineItemId || ''}
                    totalPriceOfEntity={lineItem?.totalPriceOfApps}
                    loading={loading}
                    isAddOnChangesEnabled={isAddOnChangesEnabled}
                    currency={currency}
                  />
                )}
                {/** grouping addon under products section */}
                {isAddOnChangesEnabled && lineItem?.addOnLineItems && lineItem?.addOnLineItems.length > 0 && (
                  <QuoteIntegratedEntity
                    lineItems={lineItem.addOnLineItems}
                    lineItemId={lineItem.lineItemId || ''}
                    totalPriceOfEntity={lineItem?.totalPriceOfAddOns}
                    loading={loading}
                    isAddOn={true}
                    isAddOnChangesEnabled={isAddOnChangesEnabled}
                    currency={currency}
                  />
                )}
              </CustomPanel>
            </div>
          </>
        );
      })}
    </>
  );
};
